import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomModal from '../../../../configuracion/components/customModal';
import TableContent from '../../../../documentostributarios/modalDetalleDocumento/components/TableContent';
import ExchangeMatchModal from './ExchangeMatchModal';
import { formatMoneyWithCurrency, cleanRut } from '../../../../../utils/formatter';
import moment from 'moment';
import { GetTransaction } from '../../../../../graphql/conciliacion';
import { FindDocumentById } from '../../../../../graphql/documents';
import { importOpenSidebar } from '../../../../../redux/actions/openSidebar';
import { Pagination, Dropdown, Button, Menu } from 'semantic-ui-react';
import { getObligationTypeDisplayLabel } from './components/matchRowHelpers';
import './VincularDocumentoModal.scss';

const VincularDocumentoModal = ({ open, onClose, data, onSuccess }) => {
  const dispatch = useDispatch();
  const { openSidebar } = useSelector((state) => state.openSidebar);
  const [totalAmounts, setTotalAmounts] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [paginationProps, setPaginationProps] = useState(null);
  
  // State for ExchangeMatch modal
  const [exchangeMatchModalOpen, setExchangeMatchModalOpen] = useState(false);
  const [selectedDocumentForExchange, setSelectedDocumentForExchange] = useState(null);
  const [matchAmount, setMatchAmount] = useState(0);
  const [bulkExchangePayload, setBulkExchangePayload] = useState(null);

  // Check if this is multiple transactions
  const selectedTransactionsArray = data?.selectedTransactions;
  const isMultipleTransactions = data?.isMultiple === true || 
                                 (selectedTransactionsArray && 
                                  Array.isArray(selectedTransactionsArray) && 
                                  selectedTransactionsArray.length > 1);
  
  // Check if data is a Transaction or a Document
  // If it has documentType, it's definitely a document
  // Otherwise, check if it has type === 'RECIEVED' or 'SPEND' (transactions)
  const isTransaction = data?.documentType 
    ? false 
    : (data?.type === 'RECIEVED' || data?.type === 'SPEND');
  
  // Get the ID for fetching fresh data
  // For multiple transactions, use first transaction's ID
  const dataId = isTransaction ? (isMultipleTransactions ? data?.selectedTransactions?.[0]?.id : data?.id) : data?._id || data?.id;

  // Fetch fresh data when modal opens
  const { data: transactionData, reexecuteQuery: refreshTransaction } = GetTransaction(
    open && isTransaction ? dataId : null
  );
  
  const { data: documentData, reexecuteQuery: refreshDocument } = FindDocumentById({
    ids: open && !isTransaction && dataId ? [dataId] : null
  });

  // Trigger fade-in animation when modal opens
  useEffect(() => {
    if (open) {
      setShouldAnimate(true);
      // Remove animation class after animation completes (600ms)
      const timer = setTimeout(() => {
        setShouldAnimate(false);
      }, 600);
      return () => clearTimeout(timer);
    } else {
      setShouldAnimate(false);
    }
  }, [open]);

  // Refresh data when modal opens
  useEffect(() => {
    if (open && dataId && !isMultipleTransactions) {
      // Refresh the appropriate query (only for single transaction/document)
      if (isTransaction) {
        refreshTransaction({ requestPolicy: 'network-only' });
      } else {
        refreshDocument({ requestPolicy: 'network-only' });
      }
    }
    // Force TableContent to refresh by updating key
    // This ensures documents list is refreshed when modal opens
    if (open) {
      setRefreshKey(prev => prev + 1);
    }
  }, [open, dataId, isTransaction, isMultipleTransactions, refreshTransaction, refreshDocument]);

  // Update Redux state with fresh data when it arrives
  // Only update if the data actually changed to prevent infinite loops
  // Use setTimeout to ensure updates happen after render phase
  useEffect(() => {
    if (open && transactionData?.getTransaction) {
      const newEditData = transactionData.getTransaction;
      const currentEditDataId = openSidebar.editData?.id;
      const currentCommentsLength = openSidebar.editData?.innerComments?.length || 0;
      const newCommentsLength = newEditData.innerComments?.length || 0;
      
      // Only update if editData actually changed
      if (currentEditDataId !== newEditData.id || currentCommentsLength !== newCommentsLength) {
        // Use setTimeout to defer the update until after render
        setTimeout(() => {
          dispatch(
            importOpenSidebar({
              ...openSidebar,
              editData: newEditData,
            })
          );
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionData?.getTransaction?.id, transactionData?.getTransaction?.innerComments?.length, open]);

  useEffect(() => {
    if (open && documentData?.findDocumentsById?.[0]) {
      const newEditData = documentData.findDocumentsById[0];
      const currentEditDataId = openSidebar.editData?._id || openSidebar.editData?.id;
      const newEditDataId = newEditData._id || newEditData.id;
      const currentCommentsLength = openSidebar.editData?.meta?.innerComments?.length || 0;
      const newCommentsLength = newEditData.meta?.innerComments?.length || 0;
      
      // Only update if editData actually changed
      if (currentEditDataId !== newEditDataId || currentCommentsLength !== newCommentsLength) {
        // Use setTimeout to defer the update until after render
        setTimeout(() => {
          dispatch(
            importOpenSidebar({
              ...openSidebar,
              editData: newEditData,
            })
          );
        }, 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData?.findDocumentsById?.[0]?._id, documentData?.findDocumentsById?.[0]?.id, documentData?.findDocumentsById?.[0]?.meta?.innerComments?.length, open]);

  // Use fresh data if available, otherwise fall back to prop data
  const currentData = isTransaction 
    ? (transactionData?.getTransaction || data)
    : (documentData?.findDocumentsById?.[0] || data);

  // Extract RUT from transaction for initial filter
  // For RECEIVED transactions, use issuerId; for SPEND transactions, use receiverId
  // For multiple transactions, don't auto-filter by RUT since they may have different counterparties
  const initialRutFilter = useMemo(() => {
    // Don't auto-filter by RUT for multiple transactions
    if (isMultipleTransactions) {
      return null;
    }
    if (isTransaction && currentData) {
      const isReceived = currentData.type === 'RECIEVED' || currentData.type === 'RECEIVED';
      const rut = isReceived ? currentData.issuer : currentData.receiver;
      return rut ? cleanRut(rut) : null;
    }
    return null;
  }, [isTransaction, currentData, isMultipleTransactions]);

  // Memoize the pagination render callback to prevent infinite loops
  const handlePaginationRender = useCallback((props) => {
    setPaginationProps(props);
  }, []);

  // Handle when an invoice is clicked for matching
  const handleInvoiceMatch = useCallback((document, amount) => {
    setBulkExchangePayload(null);
    setSelectedDocumentForExchange(document);
    setMatchAmount(amount);
    onClose();
    setTimeout(() => {
      setExchangeMatchModalOpen(true);
    }, 300);
  }, [onClose]);

  const handleBulkExchangeMatch = useCallback((payload) => {
    const primaryDocument = payload?.matchLines?.[0]?.document || null;
    setBulkExchangePayload(payload);
    setSelectedDocumentForExchange(primaryDocument);
    setMatchAmount(payload?.totalLocalCLP || 0);
    onClose();
    setTimeout(() => {
      setExchangeMatchModalOpen(true);
    }, 300);
  }, [onClose]);

  if (!currentData && !isMultipleTransactions) return null;
  
  const title = isMultipleTransactions
    ? (() => {
        const transactions = selectedTransactionsArray || [];
        const count = transactions.length;
        // Always calculate total from transactions to ensure accuracy
        const totalAmount = transactions.reduce((sum, t) => sum + Math.abs(t.remaining || t.amount || 0), 0);
        const currency = data?.currency || transactions[0]?.currency || 'CLP';
        const formattedAmount = formatMoneyWithCurrency(totalAmount, currency);
        return `${count} transacciones seleccionadas - Total: ${formattedAmount}`;
      })()
    : isTransaction 
      ? (() => {
          const amount = formatMoneyWithCurrency(currentData.remaining ?? currentData.amount, currentData.currency);
          const date = moment(currentData.stringDate || currentData.date, currentData.stringDate ? 'DD/MM/YYYY' : undefined).format('DD/MM/YYYY');
          const description = currentData.description || currentData.concept || '';
          const isReceived = currentData.type === 'RECIEVED' || currentData.type === 'RECEIVED';
          const contraparte = isReceived ? (currentData.issuerName || currentData.issuer || '') : (currentData.receiverName || currentData.receiver || '');
          return `${amount} depositado el ${date}${description ? ` | ${description}` : ''}${contraparte ? ` | Contraparte: ${contraparte}` : ''}`;
        })()
      : `${currentData.documentType === 'OBLIGATION' ? getObligationTypeDisplayLabel(currentData.meta) : (currentData.meta?.summary || currentData.meta?.typeLabel || 'Documento')}${currentData.meta?.documentId || currentData.meta?.number ? ' #' : ''}${currentData.meta?.documentId || currentData.meta?.number ? currentData.meta.documentId || currentData.meta.number : ''} con monto por pagar de ${formatMoneyWithCurrency(currentData.meta?.remaining ?? currentData.amount)} y fecha de emisión ${moment(currentData.documentDate).format('DD/MM/YYYY')}`;
  const totalDocs = paginationProps?.totalDocs || 0;
  const pageLimit = paginationProps?.limit || 1;
  const pageOffset = Math.max(0, ((paginationProps?.activePage || 1) - 1) * pageLimit);
  const startResult = totalDocs > 0 ? pageOffset + 1 : 0;
  const endResult = Math.min(pageOffset + pageLimit, totalDocs);
  return (
    <>
    <CustomModal
      open={open}
      onClose={() => {
        setTotalAmounts(0);
        onClose();
      }}
      title={title}
      content={
        open ? (
          <div className={`vincular-documento-modal ${shouldAnimate ? 'vincular-documento-modal-fade-in' : ''} vincular-documento-modal-content`} style={{ padding: '0', margin: '0', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <TableContent
              key={refreshKey}
              data={isMultipleTransactions ? (selectedTransactionsArray?.[0] || currentData) : currentData}
              setOpenCustomModal={onClose}
              totalAmounts={totalAmounts}
              setTotalAmounts={setTotalAmounts}
              onPaginationRender={handlePaginationRender}
              refreshTrigger={refreshKey}
              initialRutFilter={initialRutFilter}
              onInvoiceMatch={handleInvoiceMatch}
              onBulkExchangeMatch={handleBulkExchangeMatch}
              selectedTransactions={isMultipleTransactions ? selectedTransactionsArray : undefined}
              isMultiple={isMultipleTransactions}
              onSuccess={onSuccess}
            />
          </div>
        ) : null
      }
      modalStyle={{ 
        width: '1400px',
        minWidth: '80%',
        maxWidth: '90%', 
        minHeight: '90vh',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        margin: '0 auto',
        padding: '0',
        overflow: 'hidden'
      }}
      classNameContent="vincular-documento-modal-content-wrapper"
      size="tiny"
      classNameTitle="!tw-text-center !tw-text-[16px] !tw-font-semibold !tw-px-4 !tw-py-3"
      customActions={
        <div className="vincular-documento-modal-actions tw-flex tw-justify-between tw-items-center tw-w-full">
          <div className="tw-flex tw-items-center tw-gap-4">
            <div>
              <p className='tw-text-base tw-text-[#3C4B64] tw-font-semibold'>Total seleccionado: {formatMoneyWithCurrency(totalAmounts)}</p>
            </div>
            {paginationProps && (
              <div className="tw-flex tw-justify-center tw-items-center tw-gap-3 tw-h-[35px] vincular-documento-pagination-container tw-pt-2 tw-px-4">
                <span className="tw-text-sm tw-text-gray-500">
                  Mostrando {startResult} - {endResult} de {totalDocs} resultados
                </span>
                <div className="pagination__container tw-flex tw-items-center tw-gap-3">
                  <Menu compact className="small__dropdown">
                    <Dropdown
                      placeholder="Ver"
                      selection
                      fluid
                      options={paginationProps.itemsPerPageOptions}
                      value={paginationProps.limit}
                      onChange={(e, { value }) => {
                        paginationProps.setLimit(value);
                        paginationProps.setActivePage(1);
                      }}
                      style={{ zIndex: 2000 }}
                    />
                  </Menu>
                  <Pagination
                    activePage={paginationProps.activePage}
                    totalPages={paginationProps.totalPages || 1}
                    onPageChange={(e, { activePage }) => paginationProps.setActivePage(activePage || 1)}
                    siblingRange={1}
                    boundaryRange={0}
                    ellipsisItem={null}
                    firstItem={null}
                    lastItem={null}
                    className="small__pagination"
                  />
                </div>
              </div>
            )}
          </div>
          <div>
            <Button onClick={() => {
              setTotalAmounts(0);
              onClose();
            }}>Cancelar</Button>
          </div>
        </div>
      }
      classNameActions="vincular-documento-modal-actions-wrapper"
      dimmer={{ style: { zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.5)' } }}
    />
    
    {/* ExchangeMatch Modal */}
    <ExchangeMatchModal
      open={exchangeMatchModalOpen}
      onClose={() => {
        setExchangeMatchModalOpen(false);
        setSelectedDocumentForExchange(null);
        setMatchAmount(0);
        setBulkExchangePayload(null);
      }}
      document={selectedDocumentForExchange}
      data={currentData}
      matchAmount={matchAmount}
      isBulkMatch={Boolean(bulkExchangePayload?.matchLines?.length)}
      matchLines={bulkExchangePayload?.matchLines || []}
      totalLocalCLP={bulkExchangePayload?.totalLocalCLP || 0}
      closeDifferenceWithObligation={bulkExchangePayload?.closeDifferenceWithObligation || false}
      differenceCLP={bulkExchangePayload?.differenceCLP || 0}
      onConfirm={() => {
        setExchangeMatchModalOpen(false);
        setSelectedDocumentForExchange(null);
        setMatchAmount(0);
        setBulkExchangePayload(null);
        if (onSuccess) onSuccess();
      }}
    />
  </>
  );
};

export default VincularDocumentoModal;

